import React, { useEffect, useState } from "react";
import { AppBar, Box, Toolbar, MenuItem, Stack } from "@mui/material";
import {
  DarkModeRounded,
  LightModeRounded,
  Menu as MenuIcon,
} from "@mui/icons-material";
import { Common, Custom } from "components";
import { useApp, useTheme } from "contexts";
import { LANGUAGE_LIST } from "utils/constants";
import { LanguageDialog } from "./LanguageDialog";

type Props = {};

type SectionItem = {
  [key: string]: string;
};

export const Navbar = (props: Props) => {
  const appBarRef = React.useRef<HTMLElement | null>(null);

  const { theme, toggleTheme } = useTheme();
  const { locale, t } = useApp();

  const [onTop, setOnTop] = useState(true);
  const [langDialogOpen, setLangDialogOpen] = useState<boolean>(false);
  const [navigationMenu, setNavigationMenu] = useState<HTMLElement | null>(
    null
  );

  const sections: SectionItem = {
    overview: t.title.overview,
    tech_arsenal: t.title.tech_arsenal,
    project: t.title.project,
    repo: t.title.repo,
    history: t.title.history,
  };

  const isDark = theme.id === "dark";

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  function handleThemeClick() {
    toggleTheme();
  }

  function handleNavigationOpen(event: React.MouseEvent<HTMLElement>) {
    setNavigationMenu(event.currentTarget);
  }

  function handleNavigationClose() {
    setNavigationMenu(null);
  }

  function handleScrollTo(id: string) {
    const element = document.getElementById(id);
    if (element) {
      const { clientHeight = 0 } = (appBarRef.current as HTMLElement) || {};
      const offsetTop =
        element.getBoundingClientRect().top +
        window.pageYOffset -
        (clientHeight + 16);
      window.scrollTo({ top: offsetTop, behavior: "smooth" });
    }
    handleNavigationClose();
  }

  function handleScrollTop() {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleScroll() {
    setOnTop(window.scrollY <= 20);
  }

  const currentLang =
    LANGUAGE_LIST.find(
      (item) => item.code.toUpperCase() === locale.toUpperCase()
    ) || LANGUAGE_LIST[0];

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1100,
        width: "100%",
      }}
    >
      {/* Main TopNavBar */}
      <AppBar
        ref={appBarRef}
        position="sticky"
        elevation={0}
        sx={{
          top: 0,
          backgroundColor: isDark
            ? "rgba(19, 20, 26, 0.95)"
            : "rgba(250, 248, 255, 0.95)",
          backdropFilter: "blur(12px)",
          borderBottom: `1px solid ${theme.color.border.color}`,
          boxShadow: onTop
            ? "none"
            : `0px 2px 0px 0px ${
                isDark ? "#111217" : "rgba(25, 27, 34, 0.06)"
              }`,
          zIndex: 1110,
          transition:
            "background-color 0.35s ease, border-color 0.35s ease, box-shadow 0.2s ease",
        }}
      >
        <Toolbar
          sx={{
            maxWidth: 1200,
            width: "100%",
            mx: "auto",
            px: { xs: theme.spacing.md, md: theme.spacing.lg },
            minHeight: { xs: 60, md: 68 },
            justifyContent: "space-between",
          }}
        >
          {/* Brand Logo */}
          <Common.Logo
            color={theme.color.font.color}
            onClick={handleScrollTop}
            sx={{
              display: { xs: "none", md: "flex" },
            }}
          />

          {/* Mobile menu button */}
          <Box sx={{ display: { xs: "flex", md: "none" } }}>
            <Custom.IconButton
              size="medium"
              foreground={theme.color.font.color}
              onClick={handleNavigationOpen}
              aria-label="Navigation Menu"
            >
              <MenuIcon />
            </Custom.IconButton>
            <Custom.Menu
              id="navigation-menu"
              anchorEl={navigationMenu}
              onClose={handleNavigationClose}
              sx={{ display: { xs: "block", md: "none" } }}
            >
              {Object.keys(sections).map((key: string) => (
                <MenuItem
                  key={key}
                  onClick={() => handleScrollTo(key)}
                  sx={{
                    fontFamily: '"Space Mono", monospace',
                    fontSize: theme.font.xs,
                    textTransform: "uppercase",
                    py: theme.spacing.xs,
                  }}
                >
                  {sections[key]}
                </MenuItem>
              ))}
            </Custom.Menu>
          </Box>

          {/* Mobile logo */}
          <Common.Logo
            color={theme.color.font.color}
            onClick={handleScrollTop}
            sx={{
              display: { xs: "flex", md: "none" },
            }}
          />

          {/* Desktop Nav Links */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={theme.spacing.xs}
            sx={{ display: { xs: "none", md: "flex" } }}
          >
            {Object.keys(sections).map((key: string) => (
              <Custom.Button
                text
                key={key}
                onClick={() => handleScrollTo(key)}
                sx={{
                  color: theme.color.font.accent,
                  fontSize: theme.font.xs,
                  fontWeight: theme.font.semiBold,
                  px: theme.spacing.sm,
                  py: theme.spacing.xxs,
                  borderRadius: theme.border.square,
                  "&:hover": {
                    color: theme.color.primary.color,
                    backgroundColor: "transparent",
                  },
                }}
              >
                {sections[key]}
              </Custom.Button>
            ))}
          </Stack>

          {/* Right cluster: Language Popup Button, Theme toggle */}
          <Stack direction="row" alignItems="center" spacing={theme.spacing.sm}>
            {/* Language Selector Trigger Button */}
            <Custom.Button
              secondary
              size="small"
              onClick={() => setLangDialogOpen(true)}
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: theme.font.xxs,
                minWidth: "auto",
                px: theme.spacing.sm,
                py: theme.spacing.xxs,
                display: "inline-flex",
                alignItems: "center",
                gap: theme.spacing.xs,
              }}
              aria-label="Select Language"
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  lineHeight: 1,
                }}
              >
                <Custom.Flag
                  code={currentLang.flagCode}
                  size="s"
                  hasBorder={false}
                  alt={`${currentLang.nativeName} flag`}
                />
              </Box>
              <span>{currentLang.code}</span>
            </Custom.Button>

            {/* Theme Toggle Button */}
            <Custom.IconButton
              size="medium"
              foreground={theme.color.font.color}
              onClick={handleThemeClick}
              aria-label="Toggle light/dark theme"
              sx={{
                transition: "transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
                "&:hover": {
                  transform: "rotate(20deg)",
                },
                "&:active": {
                  transform: "rotate(90deg)",
                },
              }}
            >
              {isDark ? (
                <LightModeRounded fontSize="small" />
              ) : (
                <DarkModeRounded fontSize="small" />
              )}
            </Custom.IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Language selection modal dialog */}
      <LanguageDialog
        open={langDialogOpen}
        onClose={() => setLangDialogOpen(false)}
      />
    </Box>
  );
};
